import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { useForm, useWatch, Control } from 'react-hook-form';
import { axiosInstance } from 'services/http';
import {
  PhaseStructureTypeEnum,
  SubdivisionDto,
  SubdivisionOverrideTypeEnum,
} from '@vatos-pas/common';
import { createValidationResolver, normalizeFormNumber } from 'utils';
import { RadioButtonGroup } from './RadioButtonGroup';
import { CostStructureFields } from './cost-structure';
import { AllowPayoutAmountEditingToggle } from 'components/AllowPayoutAmountEditingToggle';
import { getAllCostFieldSources } from '../config/costStructureFields';
import {
  SubdivisionValidationContext,
  validateSubdivisionCreation,
} from '../views/subDivisionValidation';
import { SubDivisionFormValues } from '../containers/useSubDivisionContainer';

const BUTTON_SPINNER_SIZE = 24;
const VALIDATION_ERRORS_EMPTY = 0;

const formatErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unknown error';

const mapSubdivisionToFormValues = (
  subdivision: SubdivisionDto,
): SubDivisionFormValues => ({
  name: subdivision.name,
  address1: subdivision.address1,
  address2: subdivision.address2 ?? undefined,
  city: subdivision.city,
  state: subdivision.state,
  zip: subdivision.zip,
  builderId: subdivision.builderId,
  regionId: subdivision.regionId,
  active: subdivision.active,
  warrantyStatus: subdivision.warrantyStatus,
  reqBumpoutTimesheet: subdivision.reqBumpoutTimesheet ?? undefined,
  phaseStructureType: subdivision.phaseStructureType,
  overrideRegionCostPerBoard: subdivision.overrideRegionCostPerBoard ?? false,
  overrideType: subdivision.overrideType ?? SubdivisionOverrideTypeEnum.OFFSET,
  // Drywall fields
  dwHangingCostPerBoard: subdivision.dwHangingCostPerBoard,
  dwFinishingCostPerBoard: subdivision.dwFinishingCostPerBoard,
  dwSprayingCostPerBoard: subdivision.dwSprayingCostPerBoard,
  // DrywallAndPaint fields
  dwpHangingCostPerBoard: subdivision.dwpHangingCostPerBoard,
  dwpFinishCostPerBoard: subdivision.dwpFinishCostPerBoard,
  dwpPrimeCostPerSqft: subdivision.dwpPrimeCostPerSqft,
  dwpTrim1CostPerSqft: subdivision.dwpTrim1CostPerSqft,
  dwpBump1CostPerBoard: subdivision.dwpBump1CostPerBoard,
  dwpInteriorPaintCostPerSqft: subdivision.dwpInteriorPaintCostPerSqft,
  dwpBump2CostPerBoard: subdivision.dwpBump2CostPerBoard,
  dwpTrim2CostPerSqft: subdivision.dwpTrim2CostPerSqft,
  dwpFinalPaintCostPerSqft: subdivision.dwpFinalPaintCostPerSqft,
  dwpBump3CostPerBoard: subdivision.dwpBump3CostPerBoard,
  dwpCMBluetapeCostPerSqft: subdivision.dwpCMBluetapeCostPerSqft,
  dwpFinalBluetapeCostPerSqft: subdivision.dwpFinalBluetapeCostPerSqft,
  exteriorOnePercentage: normalizeFormNumber(subdivision.exteriorOnePercentage),
  exteriorTwoPercentage: normalizeFormNumber(subdivision.exteriorTwoPercentage),
  // Common cost fields
  pieceworkPercentage: normalizeFormNumber(subdivision.pieceworkPercentage),
  specialJobsPercentage: normalizeFormNumber(subdivision.specialJobsPercentage),
  garageCostFixed: subdivision.garageCostFixed,
  highCeilingPerBoard: subdivision.highCeilingPerBoard,
  screwInspectionPerBoard: subdivision.screwInspectionPerBoard,
  interiorOptionsPercentage: normalizeFormNumber(
    subdivision.interiorOptionsPercentage,
  ),
  exteriorOptionsPercentage: normalizeFormNumber(
    subdivision.exteriorOptionsPercentage,
  ),
  trimOptionPercentage: normalizeFormNumber(subdivision.trimOptionPercentage),
  geoDistanceTolerance: subdivision.geoDistanceTolerance ?? null,
  allowPayoutAmountEditing: subdivision.allowPayoutAmountEditing ?? false,
});

type PricingFormContentProps = {
  handleSubmit: () => void;
  control: Control<SubDivisionFormValues>;
  onOverrideRegionChange: (checked: boolean) => void;
  onOverrideTypeChange: (value: SubdivisionOverrideTypeEnum) => void;
  overrideRegionCostPerBoard: boolean | undefined;
  overrideType: SubdivisionOverrideTypeEnum | undefined;
  phaseStructureType: PhaseStructureTypeEnum;
  saving: boolean;
  onClose: () => void;
  classes: ReturnType<typeof useStyles>;
};

const PricingFormContent = ({
  handleSubmit,
  control,
  onOverrideRegionChange,
  onOverrideTypeChange,
  overrideRegionCostPerBoard,
  overrideType,
  phaseStructureType,
  saving,
  onClose,
  classes,
}: PricingFormContentProps) => (
  <>
    <DialogContent dividers>
      <Box mb={2}>
        <RadioButtonGroup
          source="phaseStructureType"
          disabled={true}
          choices={[
            { id: PhaseStructureTypeEnum.Drywall, label: 'Drywall' },
            {
              id: PhaseStructureTypeEnum.DrywallAndPaint,
              label: 'Drywall and Paint',
            },
          ]}
          value={phaseStructureType}
        />
      </Box>

      <Box mb={2}>
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={overrideRegionCostPerBoard || false}
              onChange={e => {
                onOverrideRegionChange(e.target.checked);
              }}
            />
          }
          label="Override Region Cost Structure"
        />
      </Box>

      {overrideRegionCostPerBoard && (
        <CostStructureFields<SubDivisionFormValues>
          phaseStructureType={phaseStructureType}
          overrideTypeDisabled={false}
          control={control}
          onOverrideTypeChange={onOverrideTypeChange}
          overrideTypeValue={overrideType}
          warrantyFooter={
            <AllowPayoutAmountEditingToggle
              control={control}
              name="allowPayoutAmountEditing"
            />
          }
        />
      )}
    </DialogContent>

    <DialogActions className={classes.dialogActions}>
      <Button
        onClick={onClose}
        color="secondary"
        data-cy="pricingModalCancelButton"
      >
        Cancel
      </Button>
      <Button
        onClick={handleSubmit}
        color="primary"
        variant="contained"
        disabled={saving}
        data-cy="pricingModalSaveButton"
      >
        {saving ? <CircularProgress size={BUTTON_SPINNER_SIZE} /> : 'Save'}
      </Button>
    </DialogActions>
  </>
);

type PricingModalProps = {
  open: boolean;
  onClose: () => void;
  subdivisionId: string | null;
};

export const PricingModal = ({
  open,
  onClose,
  subdivisionId,
}: PricingModalProps) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subdivision, setSubdivision] = useState<SubdivisionDto | null>(null);

  const validationContext: SubdivisionValidationContext = {
    region: subdivision?.region ?? null,
  };

  const { control, handleSubmit, setValue, reset } = useForm<
    SubDivisionFormValues,
    SubdivisionValidationContext
  >({
    resolver: createValidationResolver<
      SubDivisionFormValues,
      SubdivisionValidationContext
    >(validateSubdivisionCreation),
    context: validationContext,
  });

  const [overrideRegionCostPerBoard, overrideType] = useWatch({
    control,
    name: ['overrideRegionCostPerBoard', 'overrideType'],
  });

  const loadSubdivision = useCallback(async () => {
    if (!subdivisionId) return;

    setLoading(true);
    try {
      // Use axiosInstance directly to avoid polluting react-admin's Redux cache
      // which would cause list fields (builderName, regionName) to disappear
      const response = await axiosInstance.get<SubdivisionDto>(
        `/subdivision/${subdivisionId}`,
      );
      setSubdivision(response.data);
    } catch (error: unknown) {
      notify(
        `Error loading subdivision: ${formatErrorMessage(error)}`,
        'error',
      );
      onClose();
    } finally {
      setLoading(false);
    }
  }, [subdivisionId, notify, onClose]);

  useEffect(() => {
    if (open && subdivisionId) {
      loadSubdivision();
    }
    if (!open) {
      setSubdivision(null);
    }
  }, [open, subdivisionId, loadSubdivision]);

  useEffect(() => {
    if (subdivision) {
      reset(mapSubdivisionToFormValues(subdivision));
    }
  }, [subdivision, reset]);

  const prepareDataForSave = (values: SubDivisionFormValues) => {
    const dataToSave = { ...values };
    if (!dataToSave.overrideRegionCostPerBoard) {
      dataToSave.overrideType = undefined;
    }
    return dataToSave;
  };

  const validateCostStructure = async (
    values: SubDivisionFormValues,
  ): Promise<boolean> => {
    const validationPayload = {
      ...values,
      phaseStructureType:
        subdivision?.phaseStructureType ||
        PhaseStructureTypeEnum.DrywallAndPaint,
    };
    const validationErrors = await validateSubdivisionCreation(
      validationPayload,
      validationContext,
    );

    const costFields = [
      'overrideRegionCostPerBoard',
      'overrideType',
      ...getAllCostFieldSources(),
    ];

    const costErrors = Object.keys(validationErrors).filter(key =>
      costFields.includes(key),
    );

    if (costErrors.length > VALIDATION_ERRORS_EMPTY) {
      notify('The form is not valid. Please check for errors', 'error');
      return false;
    }
    return true;
  };

  const handleSave = async (values: SubDivisionFormValues) => {
    if (!subdivisionId || !subdivision) return;

    const isValid = await validateCostStructure(values);
    if (!isValid) return;

    setSaving(true);
    try {
      const dataToSave = prepareDataForSave(values);
      await dataProvider.update('subdivision', {
        data: dataToSave,
        id: subdivisionId,
        previousData: subdivision,
      });
      notify('Subdivision cost structure updated');
      refresh();
      onClose();
    } catch (error: unknown) {
      notify(`Error: ${formatErrorMessage(error)}`, 'warning');
    } finally {
      setSaving(false);
    }
  };

  const phaseStructureType =
    subdivision?.phaseStructureType || PhaseStructureTypeEnum.Drywall;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-cy="pricingModal"
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">Subdivision – Cost Structure</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {loading ? (
        <DialogContent className={classes.loadingContent}>
          <CircularProgress />
        </DialogContent>
      ) : subdivision ? (
        <PricingFormContent
          handleSubmit={handleSubmit(handleSave)}
          control={control}
          onOverrideRegionChange={checked =>
            setValue('overrideRegionCostPerBoard', checked)
          }
          onOverrideTypeChange={value => setValue('overrideType', value)}
          overrideRegionCostPerBoard={overrideRegionCostPerBoard}
          overrideType={overrideType}
          phaseStructureType={phaseStructureType}
          saving={saving}
          onClose={onClose}
          classes={classes}
        />
      ) : null}
    </Dialog>
  );
};

const useStyles = makeStyles({
  dialogActions: {
    padding: '16px 24px',
  },
  dialogTitle: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  loadingContent: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 200,
  },
});
